import type { MeetDetail } from "@seagames/contracts";
import { useQuery } from "@tanstack/react-query";
import { Link, createFileRoute } from "@tanstack/react-router";
import { Suspense, useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { ByePanel } from "../../components/console/bye-panel";
import { CompactButton } from "../../components/console/compact-button";
import { ConsoleLayout } from "../../components/console/console-layout";
import {
	type ConsoleEngineState,
	EngineBridgeProvider,
	useConsoleEngines,
	useEngineRegistry,
} from "../../components/console/engine-bridge";
import { fillTargets } from "../../components/console/fills";
import { MeetFrame } from "../../components/console/meet-frame";
import { type SearchTab, meetMode, meetSearch, meetViewOf } from "../../components/console/meet-tab";
import { MeetInfoTab } from "../../components/console/meet-info-tab";
import { RailContent } from "../../components/console/right-rail";
import { SaveBar } from "../../components/console/save-bar";
import { StatusStepper } from "../../components/console/status-stepper";
import { useSidePanel } from "../../components/console/side-panel-pref";
import { Card } from "../../components/page/card";
import { MedalsTab } from "../../components/medals/medals-tab";
import { PrintTab } from "../../components/print/print-tab";
import { useSidebarRail } from "../../components/shell/use-sidebar-rail";
import { TeamsPanel } from "../../components/start-list/teams-panel";
import { StartListEditor } from "../../components/start-list/start-list-editor";
import { Button } from "../../components/ui/button";
import { ConfirmDialog } from "../../components/ui/confirm-dialog";
import { EmptyState } from "../../components/ui/empty-state";
import { Spinner } from "../../components/ui/spinner";
import { getFamilyScreen } from "../../families/registry";
import { ApiError } from "../../lib/api/client";
import { errorMessage } from "../../lib/api/messages";
import { type MeetLock, byeState, eventStructureQuery, familyReadOnly, infoLock, meetLock, useLeaveGuard, useMeet } from "../../lib/meet";

/**
 * The meet screen — ONE page, the old OVR's way (user, 2026-10-22/23): the
 * header card (title, the toolbar), the meet form, always open (event,
 * number, stage, the "Meet status" dropdown, times, venue, remarks, medal
 * event, "Update meet"), then what the status calls for: the START LIST
 * until the meet goes live, the RESULT LIST from Live on (`meetMode`) — so
 * an operator never hunts for a tab. The side panel (standings) beside it.
 * Polls the meet by A3's rules (LIVE 10 s, UNOFFICIAL 30 s; paused while
 * typing, while a dialog is open, and in a hidden tab).
 *
 * Never lose a keystroke: the Report and Medals views are `?tab=` of this
 * one page, and the start list or results grid stays mounted (hidden) while
 * one is open — its row engine, drafts and cursor stay as they were, and so
 * do the meet form and the officials. The status
 * cannot change while results or the start list hold unsaved work. Leaving
 * with unsaved results or forms asks first, in ONE dialog (the start list
 * asks for its own).
 */
export const Route = createFileRoute("/_app/meets/$meetId/")({
	validateSearch: meetSearch,
	component: MeetScreen,
});

interface FormsDirty {
	details: boolean;
	officials: boolean;
	/** A meet status is picked but not saved. */
	status: boolean;
}

function MeetScreen() {
	useSidebarRail();
	const { meetId } = Route.useParams();
	const { tab: searchTab } = Route.useSearch();
	const { readOnly } = Route.useRouteContext();
	const { t } = useTranslation("meet");
	const tc = useTranslation().t;
	const [modal, setModal] = useState(false);
	const [forms, setForms] = useState<FormsDirty>({ details: false, officials: false, status: false });
	const registry = useEngineRegistry();
	const engines = useConsoleEngines(registry);
	const formsDirty = forms.details || forms.officials || forms.status;
	const blocker = useLeaveGuard(engines.hasUnsaved || formsDirty);
	const meet = useMeet(meetId, { paused: modal || blocker.status === "blocked" });
	const onModal = useCallback((open: boolean) => setModal(open), []);
	const onDetailsDirty = useCallback((details: boolean) => setForms((f) => (f.details === details ? f : { ...f, details })), []);
	const onOfficialsDirty = useCallback((officials: boolean) => setForms((f) => (f.officials === officials ? f : { ...f, officials })), []);
	const onStatusDirty = useCallback((status: boolean) => setForms((f) => (f.status === status ? f : { ...f, status })), []);
	const unsaved = engines.dirty + engines.conflict + engines.refused;

	const notFound = meet.error instanceof ApiError && meet.error.code === "NOT_FOUND";
	return (
		<>
			{meet.isPending ? (
				<Spinner className="size-[1.5rem] text-board" label={tc("state.loading")} />
			) : meet.isError && !meet.data ? (
				<EmptyState
					title={notFound ? t("notFound.title") : tc("routeError.title")}
					body={notFound ? t("notFound.body") : errorMessage(meet.error)}
					action={
						notFound ? (
							<Link to="/meets" className="inline-flex min-h-touch items-center rounded-btn bg-board px-[1rem] font-bold text-white">
								{t("notFound.toMeets")}
							</Link>
						) : (
							<Button variant="outline" onClick={() => void meet.refetch()}>
								{tc("actions.retry")}
							</Button>
						)
					}
				/>
			) : meet.data ? (
				<EngineBridgeProvider registry={registry}>
					<MeetBody
						key={meet.data.meet_id}
						meet={meet.data}
						searchTab={searchTab}
						roleReadOnly={readOnly}
						canPropose={!readOnly}
						engines={engines}
						onModal={onModal}
						onDetailsDirty={onDetailsDirty}
						onOfficialsDirty={onOfficialsDirty}
						onStatusDirty={onStatusDirty}
						reload={() => meet.refetch()}
					/>
				</EngineBridgeProvider>
			) : null}

			<ConfirmDialog
				open={blocker.status === "blocked"}
				title={unsaved > 0 || !formsDirty ? t("leave.title") : t("leave.formsTitle")}
				body={
					<>
						{unsaved > 0 && <p>{t("leave.body", { count: unsaved })}</p>}
						{formsDirty && <p className={unsaved > 0 ? "mt-[0.5rem]" : undefined}>{t("leave.formsBody")}</p>}
					</>
				}
				confirmLabel={t("leave.confirm")}
				cancelLabel={t("leave.stay")}
				tone="danger"
				onCancel={() => blocker.reset?.()}
				onConfirm={() => blocker.proceed?.()}
			/>
		</>
	);
}

function MeetBody({
	meet,
	searchTab,
	roleReadOnly,
	canPropose,
	engines,
	onModal,
	onDetailsDirty,
	onOfficialsDirty,
	onStatusDirty,
	reload,
}: {
	meet: MeetDetail;
	searchTab: SearchTab | undefined;
	roleReadOnly: boolean;
	/** May send OVR's suggested podium to the medal desk (a writer). OVR never awards: MSES does. */
	canPropose: boolean;
	engines: ConsoleEngineState;
	onModal: (open: boolean) => void;
	onDetailsDirty: (dirty: boolean) => void;
	onOfficialsDirty: (dirty: boolean) => void;
	onStatusDirty: (dirty: boolean) => void;
	reload: () => Promise<unknown>;
}) {
	const structure = useQuery(eventStructureQuery(meet.event.event_id));
	const fills = useMemo(
		() => fillTargets(structure.data, { meet_id: meet.meet_id, phase_id: meet.phase.phase_id, pool: meet.pool }),
		[structure.data, meet.meet_id, meet.phase.phase_id, meet.pool],
	);
	const view = meetViewOf(searchTab, meet);
	const mode = meetMode(meet.result_status);
	const lockInput = {
		roleReadOnly,
		resultStatus: meet.result_status,
		eventLockedAt: meet.event.locked_at,
		scheduleStatus: meet.schedule_status,
	};
	const lock: MeetLock | null = meetLock(lockInput);
	const info = infoLock(lockInput);
	const panel = useSidePanel();
	const [startListDirty, setStartListDirty] = useState(false);
	const locked = meet.event.locked_at !== null;

	return (
		<MeetFrame
			meet={meet}
			view={view}
			canWrite={!roleReadOnly}
			structure={structure.data}
			onModal={onModal}
			unsavedResults={engines.dirty + engines.conflict + engines.refused}
			panel={panel}
		>
			<ConsoleLayout
				panel={panel}
				main={
					<div className="flex min-w-0 flex-col gap-[0.75rem]">
						{/* The old OVR's meet form, always open above what the status calls for (kept mounted under a side view). */}
						<div hidden={view !== "meet"}>
							<MeetInfoTab
								meet={meet}
								lock={info}
								structure={structure.data}
								status={
									// Saved by its own "Save status", apart from "Update meet". A cancelled meet's result does not move until it is restored to scheduled.
									<StatusStepper
										meet={meet}
										engines={engines}
										canWrite={!roleReadOnly}
										locked={locked || meet.schedule_status === "CANCELLED"}
										structure={structure.data}
										onModal={onModal}
										startListDirty={startListDirty}
										onDirtyChange={onStatusDirty}
										variant="field"
									/>
								}
								onDetailsDirty={onDetailsDirty}
								onOfficialsDirty={onOfficialsDirty}
							/>
						</div>
						{byeState(meet) && (
							<div hidden={view !== "meet"}>
								<ByePanel meet={meet} lock={lock} busyElsewhere={engines.hasUnsaved || startListDirty} />
							</div>
						)}
						<div hidden={view !== "meet"}>
							{mode === "results" ? (
								<div className="flex flex-col gap-[0.75rem]">
									{/* A team event's teams open to their athletes here: the start list is not on screen once results are kept. */}
									<TeamsPanel meet={meet} readOnly={familyReadOnly(lockInput)} />
									<ResultsPanel meet={meet} engines={engines} lock={lock} readOnly={familyReadOnly(lockInput)} reload={reload} />
								</div>
							) : (
								<StartListEditor meet={meet} readOnly={roleReadOnly} onDirtyChange={setStartListDirty} />
							)}
						</div>
						{view === "report" && <PrintTab meet={meet} structure={structure.data} />}
						{view === "medals" && <MedalsTab meet={meet} structure={structure.data} canPropose={canPropose} />}
					</div>
				}
				rail={<RailContent meet={meet} structure={structure.data} fills={fills} fillsLoading={structure.isPending} />}
			/>
		</MeetFrame>
	);
}

/** The family screen (from the registry, unchanged) in a card, the save bar under it. */
function ResultsPanel({
	meet,
	engines,
	lock,
	readOnly,
	reload,
}: {
	meet: MeetDetail;
	engines: ConsoleEngineState;
	lock: MeetLock | null;
	readOnly: boolean;
	reload: () => Promise<unknown>;
}) {
	// Remounting the family screen after its result settings changed: a new key = a fresh engine from the reloaded meet.
	const [generation, setGeneration] = useState(0);
	// A cell holding an entry its row has not taken yet (the families keep it in the cell until Enter, Tab or
	// leaving the cell): the save bar must not say "everything is saved" meanwhile.
	const [typing, setTyping] = useState(false);
	// An entry field of the grid has focus: the save bar shows the keys only then (or while a draft exists).
	const [entryFocus, setEntryFocus] = useState(false);
	return (
		<Card className="flex flex-col">
			{engines.familyChanged && (
				<FamilyChangedPanel
					engines={engines}
					onReload={async () => {
						await reload();
						setGeneration((g) => g + 1);
					}}
				/>
			)}
			{/* Kept mounted (its engine holds the drafts) but inert until the reload. */}
			<div
				inert={engines.familyChanged !== null}
				// Edge to edge in the card (the family screens pad their own toolbars). relative + overflow-x-clip: a stray
				// absolutely-positioned element inside a family's own scrolling table (an sr-only label) must not widen
				// the page; clip, unlike hidden, keeps sticky headers working.
				className={`relative min-w-0 overflow-x-clip ${engines.familyChanged ? "pointer-events-none opacity-40" : ""}`}
				onInput={(e) => {
					if (e.target instanceof HTMLInputElement && e.target.type === "text") setTyping(true);
				}}
				onKeyDown={(e) => {
					if (e.key === "Enter" || e.key === "Escape" || e.key === "Tab") setTyping(false);
				}}
				onFocus={(e) => setEntryFocus(e.target instanceof HTMLInputElement || e.target instanceof HTMLSelectElement || e.target instanceof HTMLTextAreaElement)}
				onBlur={(e) => {
					setTyping(false);
					if (!(e.relatedTarget instanceof Node && e.currentTarget.contains(e.relatedTarget))) setEntryFocus(false);
				}}
			>
				<FamilySlot key={`${meet.meet_id}:${meet.result_family}:${generation}`} meet={meet} readOnly={readOnly} />
			</div>
			<SaveBar engines={engines} lock={lock} typing={typing} entryFocus={entryFocus} />
		</Card>
	);
}

function FamilySlot({ meet, readOnly }: { meet: MeetDetail; readOnly: boolean }) {
	const { t } = useTranslation("meet");
	// Won on a bye: nothing was played, so there is no board to fill (each family's would ask for two sides).
	if (byeState(meet)?.kind === "given") return <p className="px-[1rem] py-[1.5rem] text-muted">{t("bye.nothingToScore")}</p>;
	const Screen = getFamilyScreen(meet.result_family);
	return (
		<Suspense fallback={<Spinner className="size-[1.5rem] text-board" />}>
			<Screen meet={meet} readOnly={readOnly} />
		</Suspense>
	);
}

/** 409 STALE_VERSION { result_family }: the screen no longer matches the meet. Nothing more is sent until it reloads. */
function FamilyChangedPanel({ engines, onReload }: { engines: ConsoleEngineState; onReload: () => Promise<void> }) {
	const { t } = useTranslation("meet");
	const tc = useTranslation().t;
	const [busy, setBusy] = useState(false);
	return (
		<section role="alert" className="flex flex-col items-start gap-[0.5rem] rounded-t-card border-conflict border-b border-l-[0.25rem] bg-conflict-wash/50 px-[1rem] py-[0.75rem]">
			<h2 className="font-black text-[1rem]">{t("familyChanged.title")}</h2>
			<p className="max-w-[60ch] text-[0.8125rem] text-muted">
				{engines.familyChanged ? t("familyChanged.body", { family: tc(`family.${engines.familyChanged}`) }) : null}
			</p>
			{engines.hasUnsaved && <p className="max-w-[60ch] text-[0.8125rem] text-wait">{t("familyChanged.drafts")}</p>}
			<div className="flex flex-wrap gap-[0.5rem]">
				<CompactButton
					variant="primary"
					loading={busy}
					onClick={async () => {
						setBusy(true);
						try {
							await onReload();
						} finally {
							setBusy(false);
						}
					}}
				>
					{t("familyChanged.reload")}
				</CompactButton>
				{engines.hasUnsaved && (
					<CompactButton variant="outline" onClick={engines.discardAll}>
						{t("familyChanged.discard")}
					</CompactButton>
				)}
			</div>
		</section>
	);
}
